import { RecirculationSignal } from './LeadRouting'

export enum GlobalDataKeys {
  AcceptedFileTypesMap = 'accepted_file_types_map',
  AiFeedbackConfig = 'ai_feedback_config',
  AssignmentCoefficient = 'assignment_coefficient',
  AssignmentPolicy = 'assignment_policy',
  AutoConditioningRollout = 'auto_conditioning_rollout',
  AutoClearConditionBlacklist = 'auto_clear_condition_blacklist',
  AutoConfirmCocBlockedMilestones = 'auto_confirm_coc_blocked_milestones',
  AutoConfirmProductCodes = 'auto_confirm_product_codes',
  AutoPricingException = 'auto_pricing_exception',
  VepaAssetsValidationPagesLimit = 'vepa_assets_validation_pages_limit',
  BoomerangConfig = 'boomerang_config',
  Coefficient = 'coefficient',
  Credit = 'credit',
  EncompassPushDelay = 'encompass_push_delay',
  FallbackQuotingChannel = 'fallback_quoting_channel',
  FannieMaeDirectRollout = 'fannie_mae_direct_rollout',
  FannieMaeInstitutionIds = 'fannie_mae_institution_ids',
  FannieMaeMappings = 'fannie_mae_mappings',
  FannieMaePricingServicers = 'fannie_mae_pricing_servicers',
  InboundCallAutoAssignment = 'inbound_call_auto_assignment',
  IterableCampaigns = 'iterable_campaigns',
  LeadRoutingConfig = 'lead_routing_config',
  LeadRoutingRecirculationConfig = 'lead_routing_recirculation_config',
  PhoenixLeadRoutingConfig = 'phoenix_lead_routing_config',
  LeadSourcePhoneMapping = 'lead_source_phone_mapping',
  LoanMatch = 'loan_match',
  LockDeskHours = 'lock_desk_hours',
  LockExtensionPricing = 'lock_extension_pricing',
  LockingEnabledToggle = 'locking_enabled_toggle',
  MaxPurchasePrice = 'max_purchase_price',
  MeilisearchBackfillConfig = 'meilisearch_backfill_config',
  PaidCompensation = 'paid_compensation',
  PoliciesEligibleForAutoReassignment = 'policies_eligible_for_auto_reassignment',
  Pricing = 'pricing',
  PriorityScoringSystem = 'priority_scoring_system',
  RateSheetCreatorConfig = 'rate_sheet_creator_config',
  SageRadioPhoneNumbers = 'sage_radio_phone_numbers',
  LoanMatchNightlyServicerLeads = 'loan_match_nightly_servicer_leads',
  LoanMatchOnDemand = 'loan_match_on_demand',
  SharkTankLeadSourceExclusions = 'sharktank_lead_source_exclusions',
  SqueezeBatchJob = 'squeeze_batch_job',
  SqueezeDirectSend = 'squeeze_direct_send',
  SSNStoryblokFlow = 'ssn_storyblok_flow',
  StatesLicensed = 'states_licensed',
  EncompassGlobalDataConfig = 'encompass_global_data_config',
  FullStoryConfig = 'fullstory_config',
  SageHEUnlicensedStates = 'sage_he_unlicensed_states',
  FigureConfig = 'figure_config',
  DuplicateLeadConfig = 'duplicate_lead_config',
  FannieMaeCommitmentConfig = 'fannie_mae_commitment_config',
  FannieMaeCommitmentPaddingConfig = 'fannie_mae_commitment_padding_config',
  FannieMaeAdviceSellersConfig = 'fannie_mae_advice_sellers_config',
  FannieMaeEarlyCheckToolConfig = 'fannie_mae_early_check_tool_config',
  EarlyCheckAutomationConfig = 'early_check_automation_config',
  ConditionsSalesRestriction = 'conditions_sales_restriction',
  AprilFoolsMode = 'april_fools_mode',
  GeminiIncrementalRelease = 'gemini_incremental_release',
  CallOpportunityAlertConfig = 'call_opportunity_alert_config',
  DemoApplicationConfig = 'demo_application_config',
}

export enum AssignmentPolicy {
  AnnalyLoanCare = 'annaly_loan_care',
  AutoAssignInbound = 'auto_assign_inbound',
  BestMoney = 'best_money',
  Boomerang = 'boomerang',
  BulkReassignment = 'bulk_reassignment',
  DataScienceRouting = 'datascience_routing',
  Demo = 'demo',
  DuplicateLead = 'duplicate_lead',
  EmployeeLoans = 'employee_loans',
  Fifo = 'fifo',
  Hardcoded = 'hardcoded',
  HomeEquity = 'home_equity',
  LeadScoringRouting = 'lead_scoring_routing',
  Legacy = 'legacy',
  LenderEnabled = 'lender_enabled',
  LockDesk = 'lock_desk',
  ManualLead = 'manual_lead',
  ManualRoutingOnly = 'manual_routing_only',
  Phoenix = 'phoenix',
  PhoenixFallback = 'phoenix_fallback',
  PhoenixForceAssign = 'phoenix_force_assign',
  ProportionalLeads = 'proportional_leads',
  ProportionalRevenue = 'proportional_revenue',
  Reassignment = 'reassignment',
  ReApp = 're_app',
  Robinhood = 'robin_hood',
  RobinhoodConcierge = 'robinhood_concierge',
  ServicerLookup = 'servicer_lookup',
  SharkTank = 'sharktank',
  Squeeze = 'squeeze',
  TimezoneSkip = 'timezone_skip',
  TrippedTrid = 'tripped_trid',
}

// Daily cap on Shark Tank lead claims per loan officer; shared between
// admin-api enforcement and admin-ui display so the two can't drift.
export const SHARK_TANK_DAILY_CLAIM_LIMITS = {
  weekday: 25,
  weekend: 30,
} as const

// Stored under its own `auto_clear_condition_blacklist` global-data key (kept
// separate from `auto_conditioning_rollout`, which controls auto-CREATION of
// conditions). Free-text terms matched whole-word, case-insensitive against a
// condition's title; a match means Patrick must never auto-CLEAR it — he
// downgrades a passing validation to Reviewed and leaves the call to a human.
// Single source of truth: the terms live only here, no hard-coded default.
export interface AutoClearConditionBlacklistConfig {
  blockedTitleTerms: string[]
}

export interface AutoAssignmentEligibleSourcesConfig {
  enabledSources: string[]
}

export interface AutoReassignmentEligiblePoliciesConfig {
  enabledPolicies: AssignmentPolicy[]
}

interface BooleanScoreSystem {
  true: number
  false: number
}

export interface EncompassPushDelayConfig {
  hour: number
  minute: number
  second: number
}

interface LevelScoreSystem {
  [key: string]: number
}

export interface LoanMatch {
  refiMaxPoints: number
  purchaseMaxPoints: number
  productFamilies: string[]
  costOfLoanYears: number
  numAppsToProcess: number | 'all'
  savingsThreshold: number
  higherPmiSavingsThreshold: number
  rollInFeesAdditionalAmount: number
  executionHour?: number
  daysOfWeek?: number[]
  minFundedMonths?: number
}
export interface OperatingHours {
  dayOfWeek: number // 0-6 where 0 is Sunday
  startTime: string // Format: "HH:mm" in 24hr time
  endTime: string // Format: "HH:mm" in 24hr time
}

export interface PricingConfig {
  defaultLockPeriodRefinance: number
  defaultLockPeriodPurchase: number
}

export interface LockExtensionPricing {
  priceAdjustmentPerDay: number
}

export interface AutoPricingExceptionConfig {
  minimumRevenuePerLoan: number
  competitorLockedBonus: number
  hudLineIds: number[]
}

export interface AutoConditioningRolloutConfig {
  enabledApplicationIds: number[]
}

export interface AutoConfirmCocBlockedMilestonesConfig {
  milestones: string[]
}
export interface BoomerangConfig {
  enabled: boolean
  testMode: boolean
  maxLeadsToBoomerang: number
}

export interface CreditConfig {
  robinhood: {
    amount: number
    enabled: boolean
  }
}

export interface FannieMaeDirectExemptionsConfig {
  exemptionIds: number[]
  enabledLoanTypes: FannieMaeDirectRolloutFlags[]
}

export enum FannieMaeDirectRolloutFlags {
  PurchaseTBD = 'purchaseTbd',
  PurchaseAddress = 'purchaseAddress',
  RefinanceLimited = 'refinanceLimited',
  RefinanceCashout = 'refinanceCashout',
  SecondOrMoreHomeBuyer = 'secondOrMoreHomeBuyer',
  FHA = 'fha',
  VA = 'va',
  Conventional = 'conventional',
  SpecialRateFamily = 'specialRateFamily',
  HRHP = 'hrhp',
}

export enum FannieMaeInstitutionIdType {
  Default = 'default',
  AutomationPilot = 'automationPilot',
  DuPlus = 'duPlus',
}

export type FannieMaeInstitutionConfig = {
  [key in FannieMaeInstitutionIdType]: string
}

// Both AutomationPilot and DuPlus institution IDs are DU+ submissions:
// Automation Pilot is a subset of DU+, and DuPlus is a tracking-only alias that
// maps to the same Fannie institution. Treat either as DU+ (e.g. for the
// CX.FNMA.AUTO indicator and DU+ eligibility).
export function isDuPlusInstitution(
  institutionIdType: FannieMaeInstitutionIdType | string | null | undefined
): boolean {
  return (
    institutionIdType === FannieMaeInstitutionIdType.AutomationPilot ||
    institutionIdType === FannieMaeInstitutionIdType.DuPlus
  )
}

export interface FannieMaePricingServicer {
  servicerName: string
  servicerNumber: string
  encompassInvestorName: string
  encompassInvestorTemplateId: string
  supportsTitleAcceptance?: boolean
}

export interface FannieMaePricingServicersConfig {
  enabledServicers: FannieMaePricingServicer[]
  disabledServicers: FannieMaePricingServicer[]
}

export interface InboundCallAutoAssignmentConfig {
  enabledPolicies: AssignmentPolicy[]
  enabledSources: string[]
}

export interface PacingLimit {
  maxLeadsPerTimeWindow: number // e.g., 2 leads
  timeWindow: number // e.g., 1800 seconds (30 minutes)
}

export interface LeadRoutingConfig {
  timeToAccept: number // seconds to accept/reject lead
  pacingLimits: PacingLimit[]
}

export interface RecirculationStep {
  signal: RecirculationSignal
  // Measured from the previous step's recirculated_at; step 0 from the initial accept.
  delayMinutes: number
}

export interface RecirculationConfig {
  steps: RecirculationStep[]
  sweepLookaheadMinutes: number // SQS max delay is 900 s
  connectedCallMinSeconds: number
  activeCallDeferMinutes: number
  maxDefers: number
}

export const DEFAULT_RECIRCULATION_CONFIG: RecirculationConfig = {
  steps: [
    { signal: RecirculationSignal.Dial2, delayMinutes: 1 },
    { signal: RecirculationSignal.Dial3, delayMinutes: 45 },
    { signal: RecirculationSignal.Dial4, delayMinutes: 180 },
    { signal: RecirculationSignal.Dial5, delayMinutes: 180 },
    { signal: RecirculationSignal.Squeeze, delayMinutes: 0 },
  ],
  sweepLookaheadMinutes: 15,
  connectedCallMinSeconds: 30,
  activeCallDeferMinutes: 2,
  maxDefers: 10,
}

// Days without an entry are off. Defaults to 8am-7pm ET on weekdays.
export const DEFAULT_PHOENIX_ROUTING_HOURS: OperatingHours[] = [
  1, 2, 3, 4, 5,
].map((dayOfWeek) => ({ dayOfWeek, startTime: '08:00', endTime: '19:00' }))

export type PhoenixEnrollmentMatch = 'all' | 'any'

export type PhoenixEnrollmentCondition =
  | { type: 'leadSource'; values: string[] }
  | { type: 'hasCoborrower'; value: boolean }
  | { type: 'propertyState'; values: string[] } // StateAbbreviation values, e.g. 'CA'
  | { type: 'employmentStatus'; values: string[] } // EmploymentStatus values, e.g. 'Employed'
  | { type: 'isPrimaryLead'; value: boolean }

export interface PhoenixManagedEnrollmentPolicy {
  match: PhoenixEnrollmentMatch
  conditions: PhoenixEnrollmentCondition[]
}

export const DEFAULT_PHOENIX_MANAGED_ENROLLMENT_POLICY: PhoenixManagedEnrollmentPolicy =
  {
    match: 'all',
    conditions: [{ type: 'leadSource', values: ['BankratePrimary'] }],
  }

export interface PhoenixLeadRoutingConfig {
  timeToAccept: number
  routingHours?: OperatingHours[]
  // Phoenix-specific lead caps. When empty/unset, Phoenix LOs fall back to the
  // global LeadRoutingConfig pacing limits.
  pacingLimits?: PacingLimit[]
  enrollmentPolicy?: PhoenixManagedEnrollmentPolicy
}

export interface LeadSourcePhoneMapping {
  mappings: Record<string, string> // leadSource -> phoneNumber
  defaultPhoneNumber: string // fallback phone number
}

export type LockDeskCutoffDay =
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'

export type LockDeskCutoffTimezone =
  | 'eastern'
  | 'central'
  | 'mountain'
  | 'pacific'

export type LockDeskCutoffProduct = 'conventional' | 'jumbo' | 'arm'

export type LockDeskCutoffTime = string | null

export type LockDeskCutoffProductTimes = Record<
  LockDeskCutoffProduct,
  LockDeskCutoffTime
>

export type LockDeskCutoffTimezoneTimes = Record<
  LockDeskCutoffTimezone,
  LockDeskCutoffProductTimes
>

export type LockDeskCutoffSchedule = Record<
  LockDeskCutoffDay,
  LockDeskCutoffTimezoneTimes
>

export type LockDeskOpenProductState = Record<LockDeskCutoffProduct, boolean>

export type LockDeskOpenState = Record<
  LockDeskCutoffTimezone,
  LockDeskOpenProductState
>

/**
 * Runtime switches for the automated lock desk cutoff process. `use_new_cutoffs`
 * switches consumers from the product-family flags to the per-timezone state.
 */
export interface LockDeskCutoffControls {
  job_enabled: boolean
  slack_enabled: boolean
  apply_cutoffs_enabled: boolean
  use_new_cutoffs: boolean
}

export interface LockDeskHours {
  lock_desk_is_open_conventional_fha_va: boolean
  lock_desk_is_open_arm_jumbo: boolean
  lock_desk_open_state?: LockDeskOpenState
  automated_cutoff_times?: LockDeskCutoffSchedule
  automated_cutoff_controls?: LockDeskCutoffControls
}

export interface PriorityScoringSystem {
  call_attempts: {
    active: boolean
    scores: number[]
  }
  appointment_pending: {
    active: boolean
    scores: BooleanScoreSystem
  }
  lo_priority: {
    active: boolean
    scores: LevelScoreSystem
  }
  connected_call: {
    active: boolean
    scores: BooleanScoreSystem
  }
  lead_goal: {
    active: boolean
    scores: LevelScoreSystem
  }
}

export interface LoanMatchNightlyServicerLeadsConfig {
  entries: ServicerLeadsSourceConfig[]
}

export interface ServicerLeadsSourceConfig {
  source: string
  enabled: boolean
  batchProcessingEnabled: boolean
  batchSize: number
  maxRecordsToProcess: number
  executionHour?: number
  daysOfWeek?: number[]
  minOriginationMonths?: number
}

export type SqueezeBatchJobConfig = {
  maxAge: number
  minAge: number
  enabled: boolean
  testMode: boolean
  batchSize: number
  maxBatches: number
  unassignedEnabled: boolean
  unassignedMaxHoursOld: number
  unassignedLeadSourceExclusions?: string[]
  /** Days of the week on which the batch job is allowed to send leads to Squeeze.
   * Uses JS day-of-week numbers: 0=Sunday, 1=Monday, ..., 5=Friday, 6=Saturday.
   * If not set, defaults to Monday-Friday (1-5). */
  allowedDays?: number[]
  /** When enabled, applications with an active phoenix_managed_applications record
   * are excluded from the batch send on every day the job runs. */
  excludePhoenixLeads?: boolean
}

export interface SqueezeDirectSendSchedule {
  /** JavaScript day numbers, 0=Sunday through 6=Saturday. */
  daysOfWeek: number[]
  /** Eastern Time, HH:mm. The end is exclusive; overnight ranges are unsupported. */
  startTime: string
  endTime: string
}

export type SqueezeDirectSendLoanPurpose =
  | 'Purchase'
  | 'Refinance'
  | 'HomeEquity'

export interface SqueezeDirectSendRule {
  leadSource: string
  loanPurpose: SqueezeDirectSendLoanPurpose
  /** 0–100; the remainder follows normal routing. */
  percentage: number
  /** Null applies at any time. */
  schedule: SqueezeDirectSendSchedule | null
}

/** Real-time "send direct to Squeeze" routing config used by LO assignment. */
export interface SqueezeDirectSendConfig {
  rules: SqueezeDirectSendRule[]
}

export interface LoanMatchOnDemandConfig {
  enabled: boolean
  executionHour: number
  maxRecordsToProcess: number
  batchSize: number
  minCurrentRate?: number
}

export interface EncompassGlobalDataConfig {
  useEnhancedConditions: boolean
  enhancedConditionsTestGroup?: {
    enabled: boolean
    loanOfficerIds: number[]
  }
}

export interface FullStoryRoleSamplingRate {
  role: string
  page: string
  samplingRate: number // 0.0 to 1.0
}

export interface FullStoryConfig {
  enabled: boolean
  roleSamplingRates: FullStoryRoleSamplingRate[] // role+page+samplingRate combinations
}

export type BackfillPeriod =
  | 'all'
  | '1h'
  | '3h'
  | '6h'
  | '12h'
  | '1d'
  | '3d'
  | '1w'
  | '2w'
  | '1m'
  | '3m'

// Preset schedule options for when to start the backfill (all at 3am server local time)
export type ScheduledStartOption =
  | 'immediate'
  | 'sunday-3am'
  | 'monday-3am'
  | 'tuesday-3am'
  | 'wednesday-3am'
  | 'thursday-3am'
  | 'friday-3am'
  | 'saturday-3am'

export interface MeilisearchBackfillConfig {
  enabled: boolean
  batchSize: number
  maxRecordsToProcess: number
  // How far back to look for documents (e.g., '1d' = past day, 'all' = all time)
  backfillPeriod: BackfillPeriod
  // Total time to spread batches over in ms (0 = run immediately)
  spreadOverTime: number
  // When to start the backfill ('immediate' or preset time)
  scheduledStart: ScheduledStartOption
  // How many batches to run together before pausing (1 = even spread, >1 = clustered)
  batchesPerWindow: number
}

export interface DuplicateLeadConfig {
  enabled: boolean
  // Rolling window in hours - if previous app was created within this window, assign same LO
  rollingWindowHours: number
}

export interface FannieMaeCommitmentDaySchedule {
  // 24-hour local wall time `HH:mm`
  enableAt: string | null
  // 24-hour local wall time `HH:mm`
  disableAt: string | null
}
export interface FannieMaeCommitmentConfig {
  enabled: boolean
  // Seven entries, index = dayOfWeek (0 Sunday … 6 Saturday), same as OperatingHours.
  weeklySchedule?: FannieMaeCommitmentDaySchedule[]
  toggleTime?: string | null
  toggleType?: string | null
  /** Dollar amount at which to decide to hedge a loan. */
  decisionThreshold?: number
  /** Whether the decision threshold is applied (manual toggle only). */
  decisionThresholdEnabled?: boolean
  /**
   * When BestEx gain exceeds FNMA gain by more than this dollar amount,
   * hedge instead of committing to Fannie Mae.
   */
  bestexHedgeThreshold?: number
  /** Whether the BestEx hedge threshold is applied (manual toggle only). */
  bestexHedgeThresholdEnabled?: boolean
  /** PennyMac PennyPal pricing commitment period. */
  pennyMacCommitmentDays?: number
}

export interface FannieMaeCommitmentPaddingConfig {
  defaultPadDays: number
  defaultMaximumDays: number
}

interface CompetitiveMarginRule {
  key: string
  conditions: CompetitiveMarginRule[]
  returnValue: number
}

export interface RateSheetMiscConfig {
  untargetedRateAdjustment: number
}

export interface RateSheetServiceConfig {
  competitiveMarginRules: CompetitiveMarginRule[]
  competitiveMarginTestCases: string[]
  rateMappings: Record<string, Record<string, string>>
  categorizedRateBucketKeys: string[]
  miscConfig: RateSheetMiscConfig
}

/**
 * Percentage-based gradual rollout config for Gemini models.
 * percentage: 0–100, gated by the respective feature flag.
 *   0 = never use Gemini; 100 = always use Gemini (when flag is ON).
 * conditionFulfillment: gated by `gemini-fulfillment-enabled`
 * ocr: gated by `gemini-ocr-enabled`
 */
export interface GeminiIncrementalReleaseConfig {
  conditionFulfillment: { percentage: number }
  ocr?: { percentage: number }
}

/**
 * Controls who receives a call-opportunity Slack nudge. Shadow evaluation is
 * gated separately by the `enable-call-opportunity-evaluation` feature flag,
 * so this only decides whether a DM goes out. Matched case-insensitively
 * against `loan_officers.email` so the config stays editable without a DB lookup.
 *   `[]`       — default kill switch: evaluate and store, never DM.
 *   `[emails]` — canary to those MSLs.
 *   `'all'`    — every MSL.
 *
 * `escalatedOnly` narrows what is DMed: when true, only AI-escalated results go
 * out; when false, every substantive result does. Non-substantive results never
 * DM regardless. Defaults to false.
 */
export interface CallOpportunityAlertConfig {
  alertRecipientEmails: string[] | 'all'
  escalatedOnly: boolean
}

/**
 * Runtime parameters for Early Check automation (orchestrator job).
 * Job payload `maxRunnersToQueue` overrides the config value when present.
 * Optional job payload `dryRun: true` is for manual preview runs only;
 * automated runs are gated by the feature flag.
 */
export interface EarlyCheckAutomationConfig {
  /** Minutes to look back when selecting eligible loans. */
  lookbackMinutes: number
  /** Max runner jobs to enqueue per orchestrator run. */
  maxRunnersToQueue: number
}

/** Presence-only query parameter that tags a new app as a demo application. */
export const DEMO_APPLICATION_QUERY_PARAM = 'rvgt_demo_application'

/**
 * Personal-details values seeded onto the Simple v2 first page when the flow is
 * opened with `?rvgt_demo_application`. Keys match the Storyblok question names
 * on the `personalDetails` page, so `dateOfBirth` must stay ISO for its native
 * date input and `phone` must stay 10 bare digits for its length validation.
 * `emailLocalPart` is combined with a plus-addressed sequence number, because
 * reusing one address across demos collides with the existing Auth0 account.
 */
export const DEMO_BORROWER_PREFILL = {
  firstName: 'Alice',
  lastName: 'Firstimer',
  phone: '4155550011',
  dateOfBirth: '1990-09-27',
  emailLocalPart: 'afirstimer',
  emailDomain: 'gmail.com',
} as const

/** Builds the plus-addressed demo email for a sequence number. */
export function buildDemoBorrowerEmail(sequenceNumber: number): string {
  const { emailLocalPart, emailDomain } = DEMO_BORROWER_PREFILL
  return `${emailLocalPart}+${sequenceNumber}@${emailDomain}`
}

/** Verifying pause applied to a demo submission when none is configured. */
export const DEFAULT_DEMO_FULFILLMENT_DELAY_MS = 6_000

/**
 * Upper bound for the configured pause. The borrower portal reverts a
 * submission that produces no verdict within 10 minutes, so a longer pause
 * would let a demo condition fall back to To-do before its canned result lands.
 */
export const MAX_DEMO_FULFILLMENT_DELAY_MS = 60_000

/** Canned condition-fulfillment LLM result for demo applications. */
export interface DemoConditionFulfillmentResult {
  fulfillment_score: number
  confidence_score: number
  summary: string
  borrower_summary?: string
  reason: string
  issues: { description: string; severity: 'high' | 'medium' | 'low' }[]
}

export interface DemoConditionMatchResult {
  confidence: number
  reason: string
  summary?: string
  score: number
  suggestionReason: string
}

export interface DemoConditionMatchingMatchers {
  filenamePrefixes?: string[]
  derivedCategories?: string[]
}

/** Optional document request seeded alongside a demo condition. */
export interface DemoConditionDocumentRequestConfig {
  title?: string
  description?: string
  /** DocumentRequestStatus value; defaults to 'requested'. */
  status?: string
}

/**
 * A configurable demo condition. Only `title` is required. `fulfillmentResults`
 * are the per-attempt canned LLM responses used when `mockFulfillmentLlm` is on.
 */
export interface DemoConditionConfig {
  title: string
  description?: string
  /** ConditionCategory value; derived from the title when omitted. */
  category?: string
  /** ConditionType value; defaults to 'Underwriting'. */
  type?: string
  /** ConditionAccessLevel value ('internal' | 'external'); defaults to 'external'. */
  accessLevel?: string
  /** ConditionHandledBy value; defaults to 'Loan Processor'. */
  handledBy?: string
  /**
   * ConditionSource value; defaults to 'demo'. Must be non-'manual' for the
   * fulfillment path to auto-clear to Fulfilled; must not be 'alfie' to avoid
   * triggering the legacy-set AUS promotion guard.
   */
  source?: string
  /** EncompassConditionStatus value; defaults to 'Requested'. */
  encompassStatus?: string
  documentRequests?: DemoConditionDocumentRequestConfig[]
  fulfillmentResults?: DemoConditionFulfillmentResult[]
  matchingMatchers?: DemoConditionMatchingMatchers
  matchingResults?: DemoConditionMatchResult[]
}

/** Runtime configuration for demo applications. */
export interface DemoApplicationConfig {
  /** Allowlist of application IDs treated as demo applications. */
  enabledApplicationIds: number[]
  /** Uses configured per-attempt results in place of the fulfillment model. */
  mockFulfillmentLlm: boolean
  mockMatchingLlm: boolean
  /**
   * How long a demo submission stays in the borrower-facing "Checking your
   * documents…" state before its canned result is applied. Without a pause the
   * result lands before the portal repaints and the borrower never sees the
   * verifying step.
   */
  fulfillmentDelayMs?: number
  /**
   * Sage Team assigned when a new demo application is enrolled. Demo apps never
   * go through lead routing, so without a loan officer every loan-file review
   * fails its assignment precondition. Unset means skip that seat.
   */
  loanOfficerId?: number
  processorId?: number
  conditions: DemoConditionConfig[]
  /**
   * Last sequence number handed out for the demo borrower's plus-addressed
   * email. Incremented on every prefill so each demo run gets an address with
   * no existing Auth0 account. Absent ⇒ no number issued yet.
   */
  borrowerEmailSequence?: number
}
